import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { List, X, Palette } from "@phosphor-icons/react";
import { useQuote } from "@/lib/quote";

const LINKS = [
  { to: "/shop", label: "Shop" },
  { to: "/services", label: "Services" },
  { to: "/collections", label: "Collections" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
];

export default function Nav() {
  const { pathname } = useLocation();
  const { count, setDrawerOpen, setAtelierOpen } = useQuote();
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-ink/10 bg-ivory/95 backdrop-blur-xl" data-testid="site-nav">
        <div className="mx-auto grid h-[76px] max-w-[1400px] grid-cols-[1fr_auto_1fr] items-center px-5 sm:px-8">
          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  pathname.startsWith(l.to) ? "text-olive" : "text-ink/70 hover:text-olive"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <button className="flex h-10 w-10 items-center justify-center text-ink lg:hidden" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}>
            {open ? <X size={24} weight="light" /> : <List size={24} weight="light" />}
          </button>

          <Link to="/" data-testid="nav-logo-link" className="flex items-center justify-center px-4" aria-label="Marble and Veil home">
            <img src="/marble-and-veil-logo.png" alt="Marble & Veil" className="h-12 w-auto" width="160" height="48" />
          </Link>

          <div className="flex items-center justify-end gap-3">
            <button
              data-testid="nav-open-atelier-button"
              onClick={() => setAtelierOpen(true)}
              className="hidden items-center gap-2 border border-ink/20 px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] text-ink/80 transition-colors duration-300 hover:border-olive hover:text-olive sm:flex"
            >
              <Palette size={14} weight="light" /> Palette Atelier
            </button>
            <button
              data-testid="nav-quote-button"
              onClick={() => setDrawerOpen(true)}
              className="relative hidden bg-olive px-6 py-2.5 text-[11px] uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-olive-deep md:block"
            >
              Request a Quote
              {count > 0 && (
                <span data-testid="nav-quote-count" className="ml-2 inline-flex h-5 w-5 items-center justify-center border border-ivory/50 text-[11px] text-ivory">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ivory px-8 pt-20 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <nav className="flex flex-col" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <Link
                    to={l.to}
                    className="block border-b border-ink/10 py-4 font-display text-4xl text-ink transition-colors hover:text-olive"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="mt-8 flex flex-col gap-3">
              <button
                data-testid="nav-mobile-atelier-button"
                onClick={() => { setOpen(false); setAtelierOpen(true); }}
                className="flex items-center justify-center gap-2 border border-ink/20 py-3.5 text-[12px] uppercase tracking-[0.18em]"
              >
                <Palette size={16} weight="light" /> Open the Palette Atelier
              </button>
              <button
                data-testid="nav-mobile-quote-button"
                onClick={() => { setOpen(false); setDrawerOpen(true); }}
                className="bg-olive py-3.5 text-[12px] uppercase tracking-[0.18em] text-ivory"
              >
                Request a Quote {count > 0 ? `(${count})` : ""}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
